/**
 * Centralized Real-time Sync Service
 *
 * This service provides app-wide real-time updates by:
 * 1. Subscribing to a SINGLE broadcast channel ('db-changes')
 * 2. Database triggers broadcast table changes to this channel
 * 3. Mapping table changes to React Query cache invalidations
 *
 * This approach uses only 1 database connection instead of 60+
 * See: https://supabase.com/blog/realtime-broadcast-from-database
 *
 * Usage: Import and call initRealtimeSync() once in your App component
 */

import { supabase } from '@/integrations/supabase/client';
import { QueryClient } from '@tanstack/react-query';
import { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Comprehensive mapping of database tables to React Query keys
 * When a table changes, ALL listed query keys will be invalidated
 *
 * IMPORTANT: This mapping must include ALL query keys used across BOTH
 * web and mobile apps. If a hook uses a query key, it MUST be listed here.
 *
 * Format: tableName -> array of query key prefixes to invalidate
 */
const TABLE_TO_QUERY_MAP: Record<string, string[]> = {
  // ==========================================
  // USER & AUTH TABLES
  // ==========================================
  users: [
    'user', 'users', 'profile', 'auth', 'account', 'currentUser',
    'filmmaker', 'client', 'talent', 'dashboard', 'userEntityId',
    'verificationStatus', 'verificationRequests', 'verificationDocument',
  ],
  filmmaker: [
    'filmmaker', 'filmmakers', 'talent', 'talent-details', 'talentDetails',
    'talentDetailsOptimized', 'profile', 'dashboard', 'booking-settings',
    'filmmakerProfile', 'filmmaker-profile', 'filmmaker-overview',
    'public-filmmakers', 'featured-filmmakers', 'public-talent-details',
    'jobApplicantBasicDetails', 'jobApplicantServices', 'jobApplicantPortfolio',
    'jobApplicantEquipment', 'jobApplicantDocuments',
  ],
  client: [
    'client', 'clients', 'client-profile', 'client-dashboard', 'clientProfile',
    'clientProfileDashboard', 'clientDashboard', 'profile', 'dashboard',
    'clientWallet', 'clientTransactions', 'clientWalletWithContracts',
    'clientJobs', 'clientProjects', 'clientPayments',
  ],
  admin: ['admin', 'admin-dashboard', 'adminDashboardStats', 'userGrowthChartDataByType'],
  user_types: ['user', 'user-types', 'auth'],
  user_verification: [
    'verification', 'user', 'profile', 'verificationStatus',
    'verificationRequests', 'verificationDocument', 'platformStats',
  ],

  // ==========================================
  // CALENDAR TABLES
  // ==========================================
  calendar_events: [
    'calendar-events', 'calendar', 'events', 'calendar-widget',
    'calendar-group-events', 'dashboard',
  ],
  calendar_groups: [
    'calendar-groups', 'calendar-group', 'groups',
  ],
  calendar_group_members: [
    'calendar-group', 'calendar-groups', 'calendar-group-events',
    'calendar-group-pending-invites',
  ],
  calendar_group_feed_tokens: ['calendar-group-feed-tokens'],
  calendar_attendees: ['calendar-events', 'calendar', 'attendees'],
  calendar_invites: ['calendar-invites', 'calendar-group-pending-invites'],
  calendar_notes: ['calendar-notes', 'calendar', 'notes'],
  calendar_reminders: ['calendar-reminders', 'calendar'],
  calendar_shares: ['calendar-shares', 'calendar'],
  calendar_templates: ['calendar-templates', 'calendar'],
  calendar_event_type_colors: ['calendar-colors', 'calendar', 'event-type-colors'],
  calendar_audit_log: ['calendar-audit'],
  calendar_insights_cache: ['calendar-insights'],
  user_calendar_visibility_defaults: ['calendar-visibility', 'calendar'],

  // ==========================================
  // MESSAGING TABLES
  // ==========================================
  messages: [
    'messages', 'conversations', 'chat', 'inbox', 'chatRooms',
    'unread-count', 'dashboard', 'roomMessages', 'adminMessages', 'messageStats',
  ],
  message_attachments: ['messages', 'attachments'],
  broadcast_messages: [
    'broadcasts', 'admin-messages', 'broadcast-messages', 'adminMessages',
  ],
  broadcast_recipients: ['broadcasts', 'broadcast-recipients'],
  broadcast_message_reads: ['broadcasts', 'broadcast-reads'],

  // ==========================================
  // NOTIFICATIONS
  // ==========================================
  notifications: [
    'notifications', 'notification-count', 'unread-notifications',
    'notifications-unread-count', 'dashboard',
  ],
  push_tokens: ['push-tokens'],

  // ==========================================
  // JOB TABLES
  // ==========================================
  bid_project: [
    'jobs', 'job', 'job-details', 'job-list', 'jobDetails',
    'applications', 'dashboard', 'client-projects', 'clientProjects',
    'projectJobs', 'dashboardJobs',
  ],
  bids: [
    'bids', 'job-bids', 'applications', 'application', 'applicationDetails',
    'job-applications', 'jobApplications', 'dashboard', 'dashboardApplications',
    'applicationCheck', 'userApplications', 'bidAmount', 'activeBid',
  ],
  job_card_media: ['job', 'jobs', 'job-card-media'],
  job_deliverables: [
    'deliverables', 'job-deliverables', 'jobDeliverables', 'job', 'calendar-events',
    'bookingDeliverables', 'bookingDeliverablesStatus',
  ],
  job_dispute: ['disputes', 'job-dispute', 'admin-disputes', 'adminDisputes', 'disputeStats'],
  job_messages: ['job-messages', 'job'],
  job_reviews: [
    'reviews', 'job-reviews', 'jobReviews', 'talent-details', 'talentDetails',
    'reviewExists', 'bidProjectId', 'filmmakerReviews', 'filmmakerReviewSummary',
  ],
  job_service_types: ['job', 'jobs', 'service-types', 'serviceTypes'],
  job_timeline: ['job-timeline', 'timeline', 'job', 'calendar-events', 'projectTimeline'],
  job_delegation_drafts: ['job-delegation', 'delegation-drafts'],
  job_delegation_events: ['job-delegation', 'delegation-events'],
  job_delegations: ['job-delegation', 'delegations', 'job'],

  // ==========================================
  // BOOKING TABLES
  // ==========================================
  booking_links: [
    'booking-links', 'bookings', 'public-booking', 'booking-link-public',
  ],
  booking_requests: [
    'booking-requests', 'bookings', 'booking', 'dashboard', 'dashboardBookings',
    'bookingStatus', 'bookingPaymentStatus', 'bookingPaymentData',
  ],
  booking_settings: [
    'booking-settings', 'bookings', 'availability',
  ],
  filmmaker_availability: [
    'availability', 'filmmaker-availability', 'booking-settings',
    'calendar-events',
  ],

  // ==========================================
  // PROJECT TABLES
  // ==========================================
  client_project: [
    'client-projects', 'clientProjects', 'projects', 'project',
    'projectDetails', 'dashboard',
  ],
  client_project_jobs: [
    'project-jobs', 'projectJobs', 'client-projects', 'clientProjects', 'projects',
    'projectHiredCrew',
  ],
  contract: [
    'contracts', 'contract', 'contract-details',
    'applications', 'dashboard',
  ],
  documents: ['documents', 'application-documents'],

  // ==========================================
  // PAYMENT TABLES
  // ==========================================
  payment: [
    'payments', 'payment', 'payment-details', 'paymentDetails', 'paymentExists',
    'earnings', 'wallet', 'dashboard', 'dashboardPaymentHistory', 'payment-history',
    'paymentMethods', 'paymentHistory', 'walletBalance', 'clientPaymentData',
    'adminPaymentStats', 'adminPaymentTransactions',
  ],
  invoices: ['invoices', 'payment', 'earnings'],
  user_fund_history: ['fund-history', 'wallet', 'earnings', 'walletBalance'],
  filmmaker_banking: [
    'banking', 'payouts', 'earnings', 'filmmakerBanking', 'filmmaker-banking',
    'payment-methods',
  ],

  // ==========================================
  // PITCHING TABLES
  // ==========================================
  pitching_decks: [
    'pitching-decks', 'pitchingDecks', 'pitchingDeck', 'pitching', 'pitching-room',
    'calendar-events', 'deckAnalytics', 'pitchingRoomStats',
  ],
  pitching_ndas: ['pitching-ndas', 'pitching', 'ndaStatus'],
  pitch_submissions: [
    'pitch-submissions', 'pitchSubmissions', 'pitchSubmission',
    'userSubmission', 'pitching',
  ],
  selected_pitches: ['selected-pitches', 'pitching'],
  deck_selected_filmmakers: ['deck-filmmakers', 'pitching'],

  // ==========================================
  // PORTFOLIO TABLES
  // ==========================================
  filmmaker_portfolio: [
    'portfolio', 'filmmaker-portfolio', 'talent-details', 'talentDetails',
  ],
  portfolio_showcase: ['portfolio-showcase', 'portfolio'],

  // ==========================================
  // PROFILE & SETTINGS TABLES
  // ==========================================
  filmmaker_departments: ['departments', 'filmmaker', 'profile'],
  filmmaker_equipment: ['equipment', 'filmmaker', 'profile'],
  filmmaker_locations: ['locations', 'filmmaker', 'profile', 'filterOptions'],
  filmmakers_services: ['services', 'filmmaker', 'profile', 'talent-details', 'talentDetails'],
  client_locations: ['locations', 'client', 'profile'],
  location: ['locations', 'location', 'filterOptions'],
  email_preferences: ['email-preferences', 'emailPreferences', 'settings'],
  email_logs: ['email-logs'],
  email_queue: ['email-queue'],

  // ==========================================
  // SUBSCRIPTION TABLES
  // ==========================================
  user_subscriptions: [
    'subscription', 'subscriptions', 'user-subscription', 'user-tier',
    'subscription-usage', 'dashboard',
  ],
  subscription_plans: ['subscription-plans', 'plans'],
  subscription_usage_tracking: ['subscription-usage', 'usage'],
  pending_apple_subscriptions: ['pending-subscriptions'],
  pending_google_transactions: ['pending-transactions'],

  // ==========================================
  // REFERENCE TABLES
  // ==========================================
  service_types: ['service-types', 'serviceTypes', 'services'],
  service_type_embeddings: ['service-type-embeddings'],
  feature_flags: ['feature-flags', 'features'],
  feature_flag_whitelist: ['feature-whitelist', 'features'],
  feedback: ['feedback'],
  instagram_message_templates: ['instagram-templates'],
};

// Channel name for all database changes
const DB_CHANGES_CHANNEL = 'db-changes';

// State management
let realtimeChannel: RealtimeChannel | null = null;
let isInitialized = false;
let queryClientRef: QueryClient | null = null;
let authUnsubscribe: (() => void) | null = null;

/**
 * Handle a broadcast event from database triggers
 */
function handleBroadcastEvent(
  payload: { table: string; event: string; schema?: string },
  queryClient: QueryClient
) {
  const { table, event } = payload;

  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[RealtimeSync] Broadcast received: ${event} on ${table}`);
  }

  // Get query keys to invalidate for this table
  const queryKeys = TABLE_TO_QUERY_MAP[table];

  if (!queryKeys || queryKeys.length === 0) {
    // Unknown table - log this as it might indicate missing mapping
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[RealtimeSync] WARNING: No query keys mapped for table: ${table}`);
    }
    return;
  }

  // Invalidate all related queries
  queryKeys.forEach((queryKey) => {
    queryClient.invalidateQueries({
      queryKey: [queryKey],
      refetchType: 'active', // Only refetch if query is currently being used
    });
  });

  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[RealtimeSync] ${event} on ${table} -> invalidated: ${queryKeys.join(', ')}`);
  }
}

/**
 * Create and subscribe to the broadcast channel
 */
function createBroadcastChannel(queryClient: QueryClient): RealtimeChannel {
  const channel = supabase.channel(DB_CHANGES_CHANNEL, {
    config: {
      broadcast: { self: false },
    },
  });

  // Listen for table-change events from database triggers
  channel.on('broadcast', { event: 'table-change' }, ({ payload }) => {
    if (payload && payload.table) {
      handleBroadcastEvent(payload, queryClient);
    }
  });

  // Add error handling for debugging
  channel.on('system', {}, (payload) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[RealtimeSync] ${DB_CHANGES_CHANNEL} system event:`, payload);
    }
  });

  return channel;
}

/**
 * Initialize the real-time sync service
 * Call this once in your App component with the QueryClient instance
 */
export function initRealtimeSync(queryClient: QueryClient): () => void {
  if (isInitialized && queryClientRef === queryClient) {
    // Already initialized with same client
    return () => cleanupRealtimeSync();
  }

  // Store reference
  queryClientRef = queryClient;
  isInitialized = true;

  // Clean up any existing channel
  cleanupChannel();

  if (process.env.NODE_ENV === 'development') {
    console.log(`[RealtimeSync] Initializing with single broadcast channel: ${DB_CHANGES_CHANNEL}`);
    console.log(`[RealtimeSync] Tracking ${Object.keys(TABLE_TO_QUERY_MAP).length} tables`);
  }

  // Create the single broadcast channel
  realtimeChannel = createBroadcastChannel(queryClient);

  // Subscribe to the channel
  realtimeChannel.subscribe((status, err) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[RealtimeSync] ${DB_CHANGES_CHANNEL} status: ${status}`, err || '');
    }
    if (status === 'CHANNEL_ERROR') {
      console.error(`[RealtimeSync] ${DB_CHANGES_CHANNEL} error:`, err);
    }
  });

  // Listen for auth state changes to re-subscribe with new token
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[RealtimeSync] Auth event: ${event}, channel will use new token`);
      }
    } else if (event === 'SIGNED_OUT') {
      if (process.env.NODE_ENV === 'development') {
        console.log('[RealtimeSync] User signed out, cleaning up');
      }
      cleanupChannel();
    }
  });

  authUnsubscribe = () => subscription.unsubscribe();

  // Return cleanup function
  return () => cleanupRealtimeSync();
}

/**
 * Clean up the channel
 */
function cleanupChannel(): void {
  if (realtimeChannel) {
    supabase.removeChannel(realtimeChannel);
    realtimeChannel = null;
  }
}

/**
 * Clean up the real-time sync service
 * Call this when the app unmounts or user logs out
 */
export function cleanupRealtimeSync(): void {
  cleanupChannel();

  if (authUnsubscribe) {
    authUnsubscribe();
    authUnsubscribe = null;
  }

  isInitialized = false;
  queryClientRef = null;
}

/**
 * Force a sync for specific tables
 * Useful when you know data has changed externally
 */
export function forceSyncTables(tables: string[], queryClient?: QueryClient): void {
  const client = queryClient || queryClientRef;
  if (!client) {
    console.warn('[RealtimeSync] Cannot force sync: no QueryClient available');
    return;
  }

  tables.forEach((table) => {
    const queryKeys = TABLE_TO_QUERY_MAP[table];
    if (queryKeys) {
      queryKeys.forEach((queryKey) => {
        client.invalidateQueries({ queryKey: [queryKey] });
      });
    }
  });
}

/**
 * Get the current sync status
 */
export function getSyncStatus(): {
  isInitialized: boolean;
  channelState: string | null;
  trackedTables: number;
} {
  return {
    isInitialized,
    channelState: realtimeChannel?.state || null,
    trackedTables: Object.keys(TABLE_TO_QUERY_MAP).length,
  };
}
